import styled from '@emotion/styled';
import {Tabs, Tab, css, IconButton} from '@mui/material';
import React, {useEffect, useState, useMemo} from 'react';
import {Outlet, NavLink, useLocation, resolvePath} from 'react-router-dom';

import {LeaguePageTitle} from 'components/Leagues/styles';
import {useDigitParams, useIsMobile} from 'modules/utils';
import {useTranslation} from 'react-i18next';
import {useSelector, useDispatch} from 'react-redux';
import {getCurrentLeague, getCurrentUserId, isLeagueRequestError} from 'modules/selectors';
import {fetchLeagueById, clearCurrenLeague} from 'modules/actions/leagues';
import {useNavigate} from 'react-router';
import {Preloader} from 'components';
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';

const Wrapper = styled.div`
	padding: 0 32px;
	height: 100%;
	display: flex;
	flex-flow: column;
	overflow: hidden;
	@media (max-width: 880px) {
		padding: 0;
		overflow: hidden;
	}

	&.scrollable {
		overflow: auto;
	}
`;

const LeaguesBox = styled.div`
	display: flex;
	flex-flow: column;
	width: 100%;
	flex: 1;
	overflow: hidden;
	height: 100%;

	.scrollable & {
		overflow: visible;
	}
`;

const Container = styled.div`
	flex: 1;
	flex-flow: column;
	display: flex;
	padding-top: 24px;
	overflow: hidden;
	height: 100%;

	.scrollable & {
		overflow: visible;
	}
`;

const TabsWrapper = styled.div<{fullWidth?: boolean}>`
	border-bottom: 1px solid #3e434a;
	margin-right: 320px;
	transition: all 0.3s ease;
	@media (max-width: 1150px) {
		margin-right: 0;
	}

	${(props) =>
		props.fullWidth &&
		css`
			margin-right: 0;
		`}
`;

const useLeaguePage = (leagueId: number) => {
	const i18n = useTranslation();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const _currentLocation = useLocation();
	const isMobile = useIsMobile();
	const currentLeague = useSelector(getCurrentLeague);
	const pathName = _currentLocation.pathname;
	const rootPath = resolvePath("..", pathName);
	const [currentTabIndex, setCurrentTabIndex] = useState(-1);
	const userID = useSelector(getCurrentUserId);
	const isCommissioner = currentLeague?.userId === userID;
	const isLoadingError = useSelector(isLeagueRequestError);

	const leagueTabs = useMemo(() => {
		return [
			{
				label: i18n.t("league.tab.standings", "Table"),
				location: resolvePath("table", rootPath.pathname),
				scrollable: false,
				fullWidth: true,
			},
			{
				label: i18n.t("league.tab.invites", "Invites"),
				location: resolvePath("invites", rootPath.pathname),
				fullWidth: true,
				scrollable: true,
			},
			isCommissioner
				? {
					label: i18n.t("league.tab.settings", "Settings"),
					location: resolvePath("about", rootPath.pathname),
					fullWidth: false,
					scrollable: true,
				}
				: {
					label: i18n.t("league.tab.about", "About"),
					location: resolvePath("about", rootPath.pathname),
					fullWidth: true,
					scrollable: true,
				},
		]
	}, [i18n, isCommissioner, rootPath.pathname]);

	useEffect(() => {
		if (isLoadingError) {
			navigate("/groups");
		}
	}, [isLoadingError, navigate]);

	useEffect(() => {
		const _currentTabIndex = leagueTabs.findIndex((it) =>
			_currentLocation?.pathname?.includes(it.location.pathname)
		);

		setCurrentTabIndex(_currentTabIndex);
	}, [_currentLocation, leagueTabs]);

	useEffect(() => {
		dispatch(
			fetchLeagueById({
				id: leagueId,
			})
		);

		return () => {
			dispatch(clearCurrenLeague());
		};
	}, [dispatch, leagueId]);

	return {
		currentLeague,
		leagueTabs,
		isMobile,
		currentTabIndex,
		currentTab: leagueTabs[currentTabIndex],
		setCurrentTabIndex,
		navigate,
		isLoadingError,
	};
};

export const GroupPage: React.FC = () => {
	const {id = 0} = useDigitParams("id");
	const {
		currentTabIndex,
		currentTab,
		setCurrentTabIndex,
		leagueTabs,
		isMobile,
		currentLeague,
		navigate,
	} = useLeaguePage(id);

	return (
		<Wrapper className={currentTab?.scrollable ? "scrollable" : ""}>
			<LeaguePageTitle role={"presentation"}>
				{!!currentLeague?.name && (
					<IconButton onClick={() => navigate("../")} aria-label={"Back"}>
						<ArrowBackIosNewRoundedIcon />
						<span>{currentLeague?.name}</span>
					</IconButton>
				)}
			</LeaguePageTitle>
			<Container>
				<LeaguesBox>
					<TabsWrapper fullWidth={currentTab?.fullWidth}>
						{currentTabIndex !== -1 && (
							<Tabs
								value={currentTabIndex}
								// disabled={!currentTab}
								indicatorColor="secondary"
								variant={isMobile ? "fullWidth" : "scrollable"}
								onChange={(_evt, newValue: number) => {
									setCurrentTabIndex(newValue);
								}}
							>
								{leagueTabs.map((it) => (
									<Tab
										key={it.location.pathname}
										component={NavLink}
										label={it.label}
										wrapped={false}
										to={it.location.pathname}
										tabIndex={0}
									/>
								))}
							</Tabs>
						)}
					</TabsWrapper>

					{currentLeague ? <Outlet /> : <Preloader />}
				</LeaguesBox>
			</Container>
		</Wrapper>
	);
};

export default GroupPage;
